// @ts-nocheck
// Register the offline service worker. Only runs in production builds:
// SW + CRA dev server interact poorly (HMR fights cache), and the asset
// paths under `npm start` are different anyway. On localhost it's a no-op.
//
// Auto-update behavior (two halves that must work together):
//
// 1. DETECT a new deploy. The browser only re-checks the SW script on a
//    navigation by default, so a long-lived tab (someone who leaves the
//    book open for hours) would never notice a new version. We poll
//    `registration.update()` whenever the tab regains focus and on a slow
//    interval. Each deploy ships a byte-different SW (postbuild-sw.js
//    stamps the commit SHA into CACHE_NAME), so update() finds the new
//    worker, which installs with skipWaiting + clients.claim and takes
//    control — firing `controllerchange` on this tab.
//
// 2. APPLY it without disrupting the reader. On `controllerchange` we
//    reload, but only the moment the tab is hidden (background, tab swap,
//    screen off) — never yanking the page out from under someone who's
//    mid-sentence or listening. Next time they come back, it's fresh.
//
// The HTTP layer is the other guardrail: vercel.json serves the HTML
// shell with `max-age=0, must-revalidate`, so a plain refresh always
// fetches the current deploy's asset hashes (a stale-cached shell was
// what previously pinned tabs to an old bundle).

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (process.env.NODE_ENV !== "production") return;

  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => {
        // Half 1: make open tabs notice new deploys. Check when the tab
        // regains focus (the common "came back to it later" case) and on
        // a slow background interval as a backstop. update() is a no-op
        // when nothing changed, so it's cheap to call often-ish.
        const checkForUpdate = () => {
          registration.update().catch(() => {});
        };
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") checkForUpdate();
        });
        window.setInterval(checkForUpdate, 30 * 60 * 1000);
      })
      .catch((err) => {
        // Non-fatal: app still works without offline support.
        // eslint-disable-next-line no-console
        console.warn("Service worker registration failed:", err);
      });
  });

  // Track whether the page was ALREADY controlled by an SW when it loaded.
  // A first-time install also fires controllerchange, but in that case
  // there's no stale code to refresh away from — the JS we're running is
  // exactly what the new SW would serve. We only need to reload when an
  // UPDATE replaces a controller that was already in charge.
  const hadInitialController = !!navigator.serviceWorker.controller;
  let reloadScheduled = false;

  const scheduleReload = () => {
    if (reloadScheduled) return;
    reloadScheduled = true;
    // Reload while the user isn't looking — never yank the page out from
    // under an active reader. If the tab is currently hidden (background,
    // OS swap, laptop closed), reload right away. Otherwise wait for the
    // next visibility flip.
    if (document.visibilityState === "hidden") {
      window.location.reload();
      return;
    }
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.visibilityState === "hidden") {
          window.location.reload();
        }
      },
      { once: true },
    );
  };

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadInitialController) {
      // First-ever install on this device — no stale code to escape from.
      return;
    }
    scheduleReload();
  });
}
